import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  FormGroup,
  Grid,
  GridItem,
  Flex,
  FlexItem,
  Button,
  Stack,
  StackItem
} from '@patternfly/react-core';
import _ from 'lodash';
import { useParams, useLocation, useHistory } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';
import { modelKeys, useModelQuery } from '../queries';

import useBoolean from 'utils/hooks/useBoolean';
import useAlerts from 'utils/hooks/useAlerts';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';

import Tl from 'localization/LocalizationKeys';
import { useTranslation } from 'localization/useTranslation';
import Tabs from 'components/Tabs/Tabs';
import ModelType from 'components/Model/ModelType';
import ConfirmModalWithOrg from 'components/Modals/ConfirmModalWithOrg';
import ModelsContainer from 'components/Model/ModelsContainer';
import { filterModelsByPath, hasValidInput } from 'components/Model/ModelUtils';

import { TelemetryTab } from './TelemetryTab';
import { PropertyTab } from './PropertyTab';
import { CommandTab } from './CommandTab';
import { ComponentTab } from './ComponentTab';
import { SchemaTab } from './SchemaTab';
import queryString from 'query-string';
import { privileges } from 'utils/types';

import {
  getTelemetry,
  getProperties,
  getCommands
  // getComponents
} from 'utils/dtdl';
import {
  contentToModel,
  parseModelId,
  dtdlIdError,
  dtdlVersionError,
  saveModel,
  createModelId
} from '../utils/model';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';

import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';

export const MODEL_TYPE = Object.freeze({
  ASSET: 'asset',
  PROVIDER: 'provider',
  COMPONENT: 'component'
});


const defaultModelInfo = {
  modelType: MODEL_TYPE.ASSET,
  displayName: ''
};

export const ModelName = ({ modelId, search }) => {
  const { modelDraft } = queryString.parse(search, { parseBooleans: true });
  const { model, isLoading } = useModelQuery(modelId, modelDraft);
  return isLoading ? <BreadcrumbSkeleton /> : (model?.displayName ?? '');
};

const AdminModelPage = ({ breadcrumbs }) => {
  const T = useTranslation();
  const api = useAPI();
  const history = useHistory();
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [publishEnabled, setPublishEnabled] = useState(false);
  const [bindingEnabled, setBindingEnabled] = useState(false);
  const [isOpen, showPublishDialog, hidePublishDialog] = useBoolean();
  const newModelId = useRef(null);

  const [, setModelEnvelope] = useState({
    displayName: ''
  });
  const [savedModelEnvelope, setSavedModelEnvelope] = useState({});
  const { modelId } = useParams();
  const location = useLocation();
  const { modelDraft, newVersion, oldModelId } = queryString.parse(location.search, { parseBooleans: true });
  const [modelInfo, setModelInfo] = useState(defaultModelInfo);
  const [telemetry, setTelemetry] = useState([]);
  const [properties, setProperties] = useState([]);
  const [commands, setCommands] = useState([]);
  const [components, setComponents] = useState([]);
  const [schemas, setSchemas] = useState([]);
  // const [componentModels, setComponentModels] = useState([]);
  // const [warnBinding, setWarnBinding] = useState(false);


  const { model, isLoading } = useModelQuery(modelId, modelDraft);
  const newVersionQuery = useQuery({
    enabled: !_.isEmpty(oldModelId),
    queryKey: modelKeys.model(oldModelId),
    queryFn: createQueryRequest(api.core.getModel)
  });

  const existingModelQuery = useQuery({
    enabled: !_.isEmpty(modelInfo?.id ?? '') && !_.isEmpty(modelInfo?.version ?? ''),
    queryKey: modelKeys.model(createModelId(modelInfo?.id ?? '', modelInfo?.version ?? '')),
    queryFn: createQueryRequest(api.core.getModel)
  });

  const modelEnvelope = useMemo(() => {
    const usedModel = newVersionQuery.data ?? model;
    if (_.isEmpty(usedModel)) {
      return {};
    }
    const mainModel = usedModel.modelData[0];
    if (!_.isEmpty(mainModel)) {
      const { id, version } = parseModelId(usedModel.id);
      const modelInfo = {
        id,
        version,
        displayName: mainModel?.displayName,
        description: mainModel?.description,
        modelType: usedModel.modelType
      };
      const telemetry = contentToModel(getTelemetry({ dtdlModel: mainModel, component: '.' }));
      const properties = contentToModel(getProperties({ dtdlModel: mainModel, component: '.' }));
      const commands = contentToModel(getCommands({ dtdlModel: mainModel, component: '.' }));
      // const c = contentToModel(getComponents({
      //   model: mainModel,
      //   component: '.',
      //   pruneRoot: true
      // }));
      // const components = _.map(c, component => {
      //   const comp = _.filter(data.models, model => model.id === component.schema);
      //   if (comp.length === 1) {
      //     return {
      //       ...component,
      //       schema: comp[0]
      //     };
      //   }
      // });
      setTelemetry(telemetry);
      setProperties(properties);
      setCommands(commands);
      // setComponents(components);
      setModelInfo({ ...modelInfo, version: newVersion ?? version });
      // setSavedModelEnvelope(saveModel({ modelInfo, telemetry, properties, commands, components, schemas: {} }));
      setSavedModelEnvelope(saveModel({ modelInfo, telemetry, properties, commands, schemas: {} }));
    }
    return model;
  }, [model, newVersion, newVersionQuery.data]);

  const modelsQuery = useQuery({
    queryKey: modelKeys.models(),
    queryFn: createQueryRequest(api.core.getModels)
  });

  const componentModels = useMemo(() => {
    return _.filter(modelsQuery.data, model => model.modelType === MODEL_TYPE.COMPONENT);
  }, [modelsQuery.data]);

  const updateDraftQuery = useMutation({
    queryKey: modelKeys.model(modelId),
    mutationFn: createMutationRequest(api.core.updateModelDraft),
    onMutate: useCallback((model) => {
      return model;
    }, []),
    onSuccess: useCallback(() => {
      addSuccessAlert(T.common.save_model);
      if (newModelId.current) {
        const pathname = _.replace(location.pathname, 'new-model', newModelId.current);
        history.replace({ ...location, pathname: pathname, search: queryString.stringify({ modelDraft: true }) });
        newModelId.current = null;
      }
    }, [T.common.save_model, addSuccessAlert, history, location]),
    onError: useCallback((error) => {
      addErrorAlert(T.common.save_model_failed);
      console.error(error);
    }, [T.common.save_model_failed, addErrorAlert])
  });


  const [publishDraft, isPromotingDraft] = useApiCall({
    apiCall: api.core.promoteModelDraft,
    onSuccess: useCallback(() => {
      addSuccessAlert(T.common.published.model);
      history.replace({ ...location, search: queryString.stringify({ modelDraft: false }) });
      hidePublishDialog();
    }, [T.common.published.model, addSuccessAlert, hidePublishDialog, history, location]),
    onError: useCallback((error) => {
      addErrorAlert(T.common.published.model_failed);
      hidePublishDialog();
      console.error(error);
    }, [T.common.published.model_failed, addErrorAlert, hidePublishDialog])
  });

  const currentNames = useMemo(() => {
    const combined = [...telemetry, ...properties, ...commands, ...components];
    return _.map(combined, c => c?.name);
  }, [commands, components, properties, telemetry]);

  const modelIdError = useCallback((id) => {
    const error = dtdlIdError(id);
    if (error) {
      return true;
    }
  }, []);

  const modelVersionError = useCallback((version) => {
    const error = dtdlVersionError(version);
    if (error) {
      return true;
    }
  }, []);

  const tabs = useMemo(() => {
    const d1 = [
      {
        label: Tl.model.tab.telemetry,
        component: TelemetryTab,
        props: {
          telemetry,
          onTelemetryUpdate: setTelemetry,
          currentNames,
          modelDraft: modelDraft ?? false
        }
      },
      {
        label: Tl.model.tab.properties,
        component: PropertyTab,
        props: {
          properties,
          onPropertiesUpdate: setProperties,
          currentNames,
          modelDraft: modelDraft ?? false
        }
      },
      {
        label: Tl.model.tab.commands,
        component: CommandTab,
        props: {
          commands,
          onCommandsUpdate: setCommands,
          currentNames,
          modelDraft: modelDraft ?? false
        }
      }
    ];
    const component = {
      label: Tl.model.tab.components,
      component: ComponentTab,
      props: {
        components,
        onComponentsUpdate: setComponents,
        currentNames,
        componentModels,
        modelDraft: modelDraft ?? false
      }
    };
    const schema = {
      label: Tl.model.tab.schema,
      component: SchemaTab,
      props: {
        schemas,
        onSchemasUpdate: setSchemas,
        modelDraft: modelDraft ?? false
      }
    };
    let provider = [];
    return _.compact([
      ...d1,
      component,
      schema,
      ...provider
    ]);
  }, [telemetry, currentNames, modelDraft, properties, commands, components, componentModels, schemas]);


  const onPublish = useCallback((model, currentOrg) => {
    if (publishEnabled) {
      const modelData = {
        modelId: model.id,
        orgId: currentOrg.id
      };
      publishDraft(modelData);
    }
  }, [publishDraft, publishEnabled]);

  const onConfirmPublish = useCallback(() => {
    if (publishEnabled) {
      showPublishDialog();
    }
  }, [showPublishDialog, publishEnabled]);

  const onSave = useCallback(() => {
    if (saveEnabled) {
      const envelope = saveModel({ modelInfo, telemetry, properties, commands, components, schemas });
      setModelEnvelope(envelope);
      setSavedModelEnvelope(envelope);
      if (!modelId) {
        newModelId.current = envelope.id;
      }
      updateDraftQuery.mutate(envelope);
      history.replace({ ...location, search: queryString.stringify({ modelDraft: true }) });
    }
  }, [commands, components, history, location, modelId, modelInfo, properties, schemas, telemetry, updateDraftQuery, saveEnabled]);

  const modelForm = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        label: Tl.model.id,
        path: 'id',
        isRequired: true,
        hasError: modelIdError
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.version,
        path: 'version',
        isRequired: true,
        hasError: modelVersionError
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.displayName,
        path: 'displayName',
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXT,
        label: Tl.model.type,
        path: 'modelType',
        disabled: true
      },
      {
        type: ModelType.TEXTAREA,
        label: Tl.model.description,
        path: 'description',
        isRequired: false
      }
    ];
  }, [modelIdError, modelVersionError]);

  const changedData = useCallback(() => {
    // const envelope = saveModel({ modelInfo, telemetry, properties, commands, components, schemas });
    const envelope = saveModel({ modelInfo, telemetry, properties, commands, schemas });
    return !_.isEqual(envelope, savedModelEnvelope);
  }, [commands, modelInfo, properties, savedModelEnvelope, schemas, telemetry]);

  useEffect(function enableSave() {
    setSaveEnabled(hasValidInput(modelInfo, modelForm) && modelDraft && changedData() && (modelId || (!modelId && !existingModelQuery?.data)));
  }, [changedData, modelForm, modelDraft, modelInfo, existingModelQuery?.data, modelId]);

  useEffect(function enablePublish() {
    setPublishEnabled(hasValidInput(modelInfo, modelForm) && !saveEnabled && modelDraft && (modelId || (!modelId && !existingModelQuery?.data)));
  }, [modelForm, modelDraft, modelInfo, saveEnabled, existingModelQuery?.data, modelId]);

  useEffect(function enableBindings() {
    setBindingEnabled(hasValidInput(modelInfo, modelForm) && !saveEnabled);
  }, [modelForm, modelInfo, saveEnabled]);

  const firstSection = useMemo(() =>
    filterModelsByPath(modelForm, ['id', 'version', 'displayName']), [modelForm]);
  const secondSection = useMemo(() =>
    filterModelsByPath(modelForm, ['description', 'modelType']), [modelForm]);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/models/edit'}>
      { alerts }
      <Stack>
        <StackItem>
          <Grid hasGutter>
            <GridItem span={2} rowSpan={3}>
              <ModelsContainer models={firstSection} data={modelInfo} setData={setModelInfo} isLoading={isLoading} />
            </GridItem>
            <GridItem span={2} rowSpan={3}>
              <ModelsContainer models={secondSection} data={modelInfo} setData={setModelInfo} isLoading={isLoading} />
            </GridItem>
            <GridItem span={2} rowSpan={3}>
              <FormGroup label="">
                <Flex direction={{ default: 'column' }}>
                  {
                    (modelInfo.modelType === MODEL_TYPE.ASSET) ? (
                      <FlexItem>
                        <Button
                          variant="secondary"
                          isBlock
                          isDisabled={!bindingEnabled}
                          onClick={() => {
                            history.push({
                              pathname: `${location.pathname}/bindings`,
                              search: location.search
                            });
                          }}>
                          { T.common.bindings }
                        </Button>
                      </FlexItem>
                    ) : null
                  }
                  <FlexItem>
                    <Button
                      variant="secondary"
                      isBlock
                      isDisabled={!bindingEnabled}
                      onClick={() => {
                        const pathname = `${location.pathname}/templates`;
                        if (saveEnabled) {
                          // setGotoLocation({ pathname });
                          onSave();
                          return;
                        }
                        history.push({
                          pathname,
                          search: location.search
                        });
                      }}
                    >
                      { T.common.templates }
                    </Button>

                  </FlexItem>
                </Flex>
              </FormGroup>
            </GridItem>
            <GridItem span={2} rowSpan={3}>
              <FormGroup label="">
                <Flex direction={{ default: 'column' }}>
                  <FlexItem>
                    <Button isBlock isLoading={updateDraftQuery.isLoading} onClick={onSave}
                      isDisabled={!saveEnabled}>{ T.common.save }</Button>
                  </FlexItem>
                  <FlexItem>
                    <Button isBlock isDisabled={!publishEnabled}
                      onClick={onConfirmPublish}>{ T.admin.common.publish }</Button>
                  </FlexItem>
                  <FlexItem>
                    { /* <Button isBlock isDisabled={!((modelDraft === true) && (publishEnabled && saveEnabled))} */ }
                    <Button isBlock isDisabled={modelDraft === true}
                      onClick={() => history.push(
                        {
                          pathname: `${ location.pathname }/migrate`,
                          search: location.search
                        }
                      )}>{ T.migrate.assets }</Button>
                  </FlexItem>
                </Flex>
              </FormGroup>
            </GridItem>
          </Grid>

        </StackItem>
        <StackItem className={'tab-container'}>
          <Tabs tabs={tabs} tabClassName={'tab-content'}/>
        </StackItem>
      </Stack>
      <ConfirmModalWithOrg
        title={T.modals.confirm.publish.title}
        icon={'info'}
        isOpen={isOpen}
        isLoading={isPromotingDraft}
        confirmText={T.admin.common.publish}
        onConfirm={onPublish}
        item={modelEnvelope}
        onCancel={hidePublishDialog}
        template={T.modals.confirm.publish.content}
        args={[modelEnvelope?.displayName]}
        neededPrivilege={privileges.ModelManagement}
      />
    </ContentPage>
  );
};

export default AdminModelPage;
